import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  MenuItem,
  FormControl,
  Select,
  Button,
  Grid,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  TableCell,
  Tooltip,
} from "@mui/material";
import { Snackbar, Alert } from "@mui/material";
import jsPDF from "jspdf";
import "jspdf-autotable";
import LoadingBar from "react-top-loading-bar";
import InfoIcon from "@mui/icons-material/Info";

// ─── Small formatting/sanitizing helpers ───────────────────────────────────
const sanitizeName = (name) =>
  (name ?? "")
    .replace(/[`'"]/g, "") // strip stray backticks/quotes from bad DB entries
    .trim();

// Normalizes any value used as a lookup key (examType / subExam names).
// CRITICAL: different endpoints represent "no sub-exam" inconsistently —
// GetExamResults uses subExam: "", while GetExamSubCategory_Result can
// return subExam: null for the same case. Without this normalization,
// "" !== undefined and every marks lookup silently fails, which is why
// all marks were showing as "--" even though the data existed.
const normKey = (val) => (val ?? "").toString().trim();

const formatPercent = (val) => {
  if (val === null || val === undefined || val === "" || isNaN(val)) return "N/A";
  return `${Number(val).toFixed(2)}%`;
};

const formatMarks = (val) => {
  if (val === null || val === undefined || val === "") return "--";
  const num = Number(val);
  return isNaN(num) ? "--" : (Number.isInteger(num) ? String(num) : num.toFixed(2));
};

// Color used for grade text/badges throughout the PDF
const gradeColor = (grade) => {
  switch ((grade || "").toString().trim().toUpperCase()) {
    case "A+":
    case "A":
      return [22, 130, 60]; // green
    case "B+":
    case "B":
      return [24, 100, 140]; // blue-teal
    case "C+":
    case "C":
      return [184, 134, 11]; // amber
    case "D":
      return [204, 102, 0]; // orange
    case "E":
    case "F":
      return [178, 34, 34]; // red
    default:
      return [60, 60, 60]; // neutral gray
  }
};

const GeneratedResult = () => {
  const [isPdfLoading, setIsPdfLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [examTypes, setExamTypes] = useState([]);
  const [session, setSession] = useState("");
  const [examType, setExamType] = useState("");
  const [className, setClassName] = useState("");
  const [classes, setClasses] = useState([]);
  const [section, setSection] = useState("");
  const [sections, setSections] = useState([]);
  const [resultData, setResultData] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [permissions, setPermissions] = useState({
    uRead: 0,
    uWrite: 0,
    uModify: 0,
    uDelete: 0,
    isPermission: 0,
  });
  const [formId, setFormId] = useState(null);
  const [error, setError] = useState(null);

  const token = sessionStorage.getItem("token");
  const apiUrl = process.env.REACT_APP_BASE_URL;

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  }));

  // ─── Fetch Form ID ──────────────────────────────────────────────────────────
  const fetchFormId = async () => {
    try {
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/ExclusivePermission/Form_Authorizer",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ moduleId: 23 }),
        }
      );
      const data = await response.json();
      if (response.ok) {
        const resultForm = data.find((form) => form.formName === "Get Result");
        if (resultForm) {
          setFormId(resultForm.formId);
        } else {
          setError("Get Result form not found in response");
          setIsLoading(false);
        }
      } else {
        setError("Failed to fetch form ID");
        setOpenSnackbar(true);
        setSnackbarMessage("Failed to fetch form ID");
        setIsLoading(false);
      }
    } catch (error) {
      console.error("Error fetching form ID:", error);
      setError("Error fetching form ID");
      setOpenSnackbar(true);
      setSnackbarMessage("Error fetching form ID");
      setIsLoading(false);
    }
  };

  // ─── Fetch Permissions ──────────────────────────────────────────────────────
  const fetchPermissions = async () => {
    if (!formId) return;
    try {
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ formId }),
      });
      const data = await response.json();
      if (response.ok && data.length > 0) {
        setPermissions(data[0]);
      } else {
        setPermissions({ ...permissions, isPermission: 0 });
        setError("Failed to fetch permissions");
        setOpenSnackbar(true);
        setSnackbarMessage("Failed to fetch permissions");
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      setError("Error fetching permissions");
      setOpenSnackbar(true);
      setSnackbarMessage("Error fetching permissions");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setResultData([]);
  }, [session, className, section]);

  const fetchSessions = async () => {
    try {
      setLoadingBarProgress(30);
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/ClassPromotion/GetFinancialYear",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
        }
      );
      if (!response.ok) { setLoadingBarProgress(0); return; }
      setLoadingBarProgress(100);
      const data = await response.json();
      if (Array.isArray(data)) {
        setSessions(data);
        const today = new Date();
        const currentYear = today.getFullYear();
        const nextYear = currentYear + 1;
        const financialYearStart = new Date(currentYear, 3, 1);
        const financialYearEnd = new Date(nextYear, 2, 31);
        const currentSessionString =
          today >= financialYearStart && today <= financialYearEnd
            ? `${currentYear}-${nextYear}`
            : `${currentYear - 1}-${currentYear}`;
        const currentSession = data.find(
          (item) => item.finanacialYear === currentSessionString
        );
        if (currentSession) setSession(currentSession.financialYearID);
      }
    } catch (error) {
      console.error("Error fetching financial years:", error);
    }
  };

  const fetchExamTypes = async () => {
    try {
      const response = await fetch(`${apiUrl}/Exam/ExamList`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setExamTypes(data.map((item) => ({ id: item.examTypeId, name: item.examType })));
      }
    } catch (error) {
      console.error("Error fetching exam types:", error);
    }
  };

  const fetchClasses = async () => {
    try {
      const response = await fetch(`${apiUrl}/Enquiry/ddlClassName`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({}),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setClasses(data.map((item) => ({ id: item.classId, name: item.className })));
      }
    } catch (error) {
      console.error("Error fetching class names:", error);
    }
  };

  const fetchSections = async (classId) => {
    try {
      const response = await fetch(`${apiUrl}/Exam/ddlSection_clsId`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ classId }),
      });
      if (!response.ok) return;
      const data = await response.json();
      if (Array.isArray(data)) {
        setSections(data.map((item) => ({ id: item.sectionId, name: item.sectionName })));
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
    }
  };

  const handleClassChange = (event) => {
    const selectedClass = event.target.value;
    setClassName(selectedClass);
    if (selectedClass) fetchSections(selectedClass);
    else setSections([]);
  };

  const handleGetResult = async () => {
    if (!session || !className || !section) {
      setSnackbarMessage("Please select all fields!");
      setOpenSnackbar(true);
      return;
    }
    try {
      setIsLoading(true);
      const response = await fetch(
        "https://wsmsv1.aidigitalsec.com/api/Result/GetStudentResultsGenerated",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            sessionId: session,
            examTypeId: 0,
            classId: className,
            sectionId: section,
          }),
        }
      );
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        setResultData(data);
      } else {
        setSnackbarMessage("No data available for the selected class.");
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error fetching result data.");
      setOpenSnackbar(true);
      console.error("Error fetching result data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // ─── MAIN PRINT HANDLER ─────────────────────────────────────────────────────
  // Flow:
  //   1. GET student profile  → objData (profile), totalAttendancList[0] (yearly attendance)
  //   2. GET exam results     → results[], grosstotalMarks[0] (overall grade/%), totalMarks1[] (per-subject totals)
  //   3. GET exam criteria    → list of examType headers
  //   4. For each exam type, GET sub-categories → sub-exam column names
  //   5. Build PDF from all collected data
  //
  // DATA FIXES applied here:
  //   - The API returns a "ghost" subject row (subjectId: 0, subjectName: "")
  //     which is really a per-exam-type total, not a real subject — it is
  //     now filtered out everywhere (subjectTotals, marksData).
  //   - Subject names are sanitized (e.g. "Hindi`" -> "Hindi") since the DB
  //     occasionally has stray characters baked into the name.
  //   - Percentages/marks are formatted consistently via formatPercent/formatMarks.
  //   - A real overall Pass/Fail status is computed (any subject grade E/F ⇒ FAIL).
  const fetchStudentProfile = async (studentId) => {
    try {
      setIsPdfLoading(true);
      setLoadingBarProgress(30);

      // ── Step 1: Student profile + attendance ──────────────────────────────
      const profileResponse = await fetch(`${apiUrl}/Attendance/StudentProfile`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ yearId: session, studentId }),
      });
      if (!profileResponse.ok) throw new Error("Failed to fetch student profile");
      const profileData = await profileResponse.json();

      const studentProfile = profileData.objData;
      const studentAttendance = profileData.totalAttendancList?.[0] ?? null;

      // ── Step 2: Exam results ──────────────────────────────────────────────
      const resultsResponse = await fetch(`${apiUrl}/Result/GetExamResults`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          studentId,
          sessionId: session,
          examTypeId: 0,
          subExamId: 0,
          subjectId: 0,
        }),
      });
      if (!resultsResponse.ok) {
        setLoadingBarProgress(0);
        throw new Error("Failed to fetch exam results");
      }
      setLoadingBarProgress(70);
      const resultsData = await resultsResponse.json();

      const results = resultsData.results ?? [];

      // FIX: drop the "ghost" per-exam-type total row (subjectId 0 / blank name)
      // and sanitize real subject names.
      const subjectTotals = (resultsData.totalMarks1 ?? [])
        .filter((s) => s.subjectName && sanitizeName(s.subjectName) !== "")
        .map((s) => ({ ...s, subjectName: sanitizeName(s.subjectName) }));

      const grossTotal = resultsData.grosstotalMarks?.[0] ?? {};
      const overallGrade = grossTotal.totalGrade ?? "N/A";
      const totalPercentage =
        grossTotal.overallPercentage ?? grossTotal.averagePercentage ?? "N/A";

      const teacherRemarks = results[0]?.teacherRemarks ?? "N/A";

      const subjectNames = subjectTotals.map((s) => s.subjectName);

      // Real overall result status — FAIL if any subject's grade is E/F
      const hasFailingGrade = subjectTotals.some((s) =>
        ["E", "F"].includes((s.totalGrade || "").toString().trim().toUpperCase())
      );
      const resultStatus = hasFailingGrade ? "FAIL" : "PASS";

      // ── Step 3: Exam criteria ─────────────────────────────────────────────
      const examCriteriaResponse = await fetch(
        `${apiUrl}/Result/GetExamCriteria_result`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({ studentId }),
        }
      );
      if (!examCriteriaResponse.ok) throw new Error("Failed to fetch exam criteria");
      const examCriteriaData = await examCriteriaResponse.json();

      // ── Step 4: Build examHeadersData using GetExamSubCategory_Result API ──
      const subCategoryPromises = examCriteriaData.map(async (exam) => {
        const subCategoryResponse = await fetch(
          `${apiUrl}/Result/GetExamSubCategory_Result`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token },
            body: JSON.stringify({
              studentId,
              examTypeId: exam.examCategoryId,
            }),
          }
        );

        if (!subCategoryResponse.ok) {
          console.error(`Failed to fetch sub-category for exam type ${exam.examType}`);
          return {
            examType: exam.examType?.trim(),
            examCategoryId: exam.examCategoryId,
            subCategories: [],
          };
        }

        const subCategoryData = await subCategoryResponse.json();

        const subCategories = subCategoryData.map((sub) => {
          const subExamName = normKey(sub.subExam);
          const matchingResult = results.find(
            (r) => r.examTypeId === exam.examCategoryId && normKey(r.subExam) === subExamName
          );
          return {
            name: subExamName,
            maxMarks: matchingResult?.maxMarks ?? "",
            minMarks: matchingResult?.minMarks ?? "",
          };
        });

        return {
          examType: normKey(exam.examType),
          examCategoryId: exam.examCategoryId,
          subCategories,
        };
      });

      const examHeadersData = await Promise.all(subCategoryPromises);

      // ── Step 4b: Safety net — never drop a recorded mark ────────────────────
      // examHeadersData (table COLUMNS) comes from GetExamCriteria_result /
      // GetExamSubCategory_Result, which is a *separate* source of truth from
      // the actual `results` records. If that criteria API is missing an exam
      // type/sub-exam that a student actually has marks for (stale config,
      // retired exam type, etc.), the mark exists in marksData but has no
      // column to render in — so it silently never shows up, even though the
      // lookup itself succeeds. To guarantee every real mark is shown, we
      // backfill any examType/subExam combo found in `results` that isn't
      // already covered by the criteria-based headers.
      const existingCombos = new Set();
      examHeadersData.forEach((exam) => {
        exam.subCategories.forEach((sub) => {
          existingCombos.add(`${exam.examType}|||${sub.name}`);
        });
      });

      results.forEach((r) => {
        if (r.subjectId === 0) return; // skip the ghost total row
        const examTypeKey = normKey(r.examType);
        const subExamKey = normKey(r.subExam);
        const comboKey = `${examTypeKey}|||${subExamKey}`;
        if (existingCombos.has(comboKey)) return;

        existingCombos.add(comboKey);
        let examEntry = examHeadersData.find((e) => e.examType === examTypeKey);
        if (!examEntry) {
          examEntry = { examType: examTypeKey, examCategoryId: r.examTypeId, subCategories: [] };
          examHeadersData.push(examEntry);
        }
        examEntry.subCategories.push({
          name: subExamKey,
          maxMarks: r.maxMarks ?? "",
          minMarks: r.minMarks ?? "",
        });
      });

      // ── Step 5: Build marks lookup (keys normalized, not just trimmed) ─────
      const marksData = {};
      results.forEach((r) => {
        // Skip the same ghost row here too (subjectId 0 / blank name)
        if (r.subjectId !== 0 && r.subjectName) {
          const subjectKey = sanitizeName(r.subjectName);
          const examTypeKey = normKey(r.examType);
          const subExamKey = normKey(r.subExam);

          if (!marksData[subjectKey]) marksData[subjectKey] = {};
          if (!marksData[subjectKey][examTypeKey]) marksData[subjectKey][examTypeKey] = {};
          marksData[subjectKey][examTypeKey][subExamKey] = r.obtainedMarks;
        }
      });

      // ── Step 6: Build subjectTotalsMap ───────────────────────────────────
      const subjectTotalsMap = {};
      subjectTotals.forEach((s) => {
        subjectTotalsMap[s.subjectName] = s;
      });

      const updatedStudentProfile = {
        ...studentProfile,
        teacherRemarks,
        grade: overallGrade,
        totalPercentage,
        resultStatus,
      };

      const selectedSession = sessions.find((s) => s.financialYearID === session);
      const sessionYear = selectedSession?.finanacialYear ?? "N/A";

      setLoadingBarProgress(100);

      handlePrint(
        updatedStudentProfile,
        studentAttendance,
        subjectNames,
        examHeadersData,
        marksData,
        subjectTotalsMap,
        sessionYear
      );
    } catch (error) {
      setIsPdfLoading(false);
      setLoadingBarProgress(0);
      alert("Error generating PDF: " + error.message);
      console.error("API request error:", error);
    }
  };

  // ─── PDF GENERATION — professional report-card layout ─────────────────────
  const handlePrint = async (
    student,
    attendance,         // totalAttendancList[0] — yearly aggregate
    subjectNames,        // unique, sanitized subject names (from totalMarks1)
    examHeadersData,      // [{ examType, subCategories: [{ name, maxMarks, minMarks }] }]
    marksData,            // marksData[subject][examType][subExam] = obtainedMarks
    subjectTotalsMap,     // totalMarks1 keyed by sanitized subjectName
    sessionYear
  ) => {
    try {
      const doc = new jsPDF("l", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();   // 297
      const pageHeight = doc.internal.pageSize.getHeight(); // 210

      const NAVY = [11, 31, 64];
      const TEAL = [0, 128, 128];
      const LIGHT_BG = [245, 247, 250];

      // Outer border
      const borderMargin = 10;
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.6);
      doc.rect(borderMargin, borderMargin, pageWidth - 2 * borderMargin, pageHeight - 2 * borderMargin);

      // Thin inner accent border for a "certificate" feel
      doc.setDrawColor(190, 171, 108); // gold
      doc.setLineWidth(0.2);
      doc.rect(
        borderMargin + 1.5,
        borderMargin + 1.5,
        pageWidth - 2 * (borderMargin + 1.5),
        pageHeight - 2 * (borderMargin + 1.5)
      );

      // ── Logo & School Header ──────────────────────────────────────────────
      let logoDataUrl = null;
      try {
        const placeholderLogo =
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUGFdjYGBgYAAAAAUAAY+SkSAAAAAASUVORK5CYII=";
        doc.setFillColor(255, 255, 255);
        doc.rect(13, 16, 34, 34, "F");
        doc.addImage(placeholderLogo, "PNG", 17, 22, 21, 21);

        const schoolResponse = await fetch(
          "https://wsmsv1.aidigitalsec.com/api/FeeReport/GetSchoolName",
          {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: token },
            body: JSON.stringify({}),
          }
        );
        if (!schoolResponse.ok) throw new Error("School API failed");
        const apiData = await schoolResponse.json();
        const address = apiData?.[0]?.address ?? "N/A";
        const phoneNo = apiData?.[0]?.phoneNo ?? "N/A";
        const schoolName = apiData?.[0]?.schoolName ?? "N/A";

        doc.setFont("times", "bold");
        doc.setFontSize(22);
        doc.text(schoolName.replace(/['"]+/g, ""), pageWidth / 2, 25, null, null, "center");

        doc.setFontSize(11);
        doc.setFont("times", "normal");
        const splitIndex = Math.ceil(address.length / 2);
        const splitPoint =
          address.lastIndexOf(" ", splitIndex) !== -1 ? address.lastIndexOf(" ", splitIndex) : splitIndex;
        const addr1 = address.substring(0, splitPoint).trim().replace(/['"]+/g, "");
        const addr2 = address.substring(splitPoint).trim().replace(/['"]+/g, "");
        doc.text(addr1, pageWidth / 2, 34, null, null, "center");
        doc.text(addr2, pageWidth / 2, 40, null, null, "center");
        doc.text("Contact No.: " + phoneNo.replace(/['"]+/g, ""), pageWidth / 2, 46, null, null, "center");

        const logoImg = apiData?.[0]?.logoImg;
        if (logoImg && typeof logoImg === "string" && logoImg.trim() !== "") {
          let cleanedBase64 = logoImg.replace(/^data:image\/[a-zA-Z]+;base64,/, "").replace(/\s|\n/g, "");
          cleanedBase64 += "=".repeat((4 - (cleanedBase64.length % 4)) % 4);
          if (/^[A-Za-z0-9+/=]+$/.test(cleanedBase64)) {
            logoDataUrl = `data:image/png;base64,${cleanedBase64}`;
            const apiImg = new Image();
            await new Promise((res, rej) => {
              apiImg.onload = res;
              apiImg.onerror = () => rej(new Error("Failed to load logo"));
              apiImg.src = logoDataUrl;
            });
            doc.setFillColor(255, 255, 255);
            doc.rect(13, 16, 34, 34, "F");
            doc.addImage(logoDataUrl, "PNG", 15, 18, 30, 30);
          }
        }
      } catch (err) {
        console.error("Logo/school header error:", err.message);
        doc.setFont("times", "bold");
        doc.setFontSize(10);
        doc.setTextColor(255, 0, 0);
        doc.text("Logo Failed", 15, 50);
        doc.setTextColor(0, 0, 0);
      }

      // PASS / FAIL stamp badge — top right corner
      const isPass = (student.resultStatus ?? "").toUpperCase() !== "FAIL";
      const stampColor = isPass ? [22, 130, 60] : [178, 34, 34];
      doc.setFillColor(...stampColor);
      doc.roundedRect(pageWidth - 48, 15, 32, 12, 2, 2, "F");
      doc.setFont("times", "bold");
      doc.setFontSize(12);
      doc.setTextColor(255, 255, 255);
      doc.text(isPass ? "PASS" : "FAIL", pageWidth - 32, 23, null, null, "center");
      doc.setTextColor(0, 0, 0);

      // Divider
      doc.setDrawColor(...TEAL);
      doc.setLineWidth(1.0);
      doc.line(20, 50, pageWidth - 20, 50);

      // Report title
      doc.setFontSize(13);
      doc.setFont("times", "bold");
      doc.text(`Progress Report ${sessionYear}`, pageWidth / 2, 56, null, null, "center");

      // Student name
      doc.setFontSize(15);
      doc.text(`${student.studentName ?? "N/A"}`, pageWidth / 2, 66, null, null, "center");
      doc.setDrawColor(128, 128, 128);
      doc.setLineWidth(0.3);
      doc.line(pageWidth / 2 - 40, 70, pageWidth / 2 + 40, 70);

      // ── Student details (light "info card" panel, two-column layout) ──────
      const panelY = 74;
      const panelH = 32;
      doc.setFillColor(...LIGHT_BG);
      doc.roundedRect(18, panelY, pageWidth - 36, panelH, 2, 2, "F");

      doc.setFont("times", "normal");
      doc.setFontSize(11);
      const labelWidth = 35;
      const colLeft = 24;
      const colRight = pageWidth / 2 + 10;

      const leftDetails = [
        ["Father's Name:", student.fathersName ?? "N/A"],
        ["Mother's Name:", student.mothersName ?? "N/A"],
        ["Date of Birth:", student.studentDOB ?? "N/A"],
      ];
      const rightDetails = [
        ["Class & Section:", `${student.className ?? "N/A"} - ${student.sectionName ?? "N/A"}`],
        ["Roll No.:", String(student.rollNo ?? "N/A")],
        ["Admission No.:", String(student.admissionNo ?? "N/A")],
      ];

      let currentY = panelY + 8;
      const lineH = 8;

      leftDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colLeft, currentY);
        doc.setFont("times", "normal");
        doc.text(String(value), colLeft + labelWidth, currentY);
        currentY += lineH;
      });

      let rightY = panelY + 8;
      rightDetails.forEach(([label, value]) => {
        doc.setFont("times", "bold");
        doc.text(label, colRight, rightY);
        doc.setFont("times", "normal");
        doc.text(String(value), colRight + labelWidth, rightY);
        rightY += lineH;
      });

      // ── Marks Table ───────────────────────────────────────────────────────
      // Columns: S.No | Subject | [ExamType > SubExam ...] | Total Obt | Total Max | Grade
      let lastY = panelY + panelH + 6;
      const minMargin = 15;

      if (examHeadersData.length > 0 && subjectNames.length > 0) {
        const headerRow1 = [
          { content: "S.No", rowSpan: 2, styles: { halign: "center" } },
          { content: "Subject", rowSpan: 2, styles: { halign: "center" } },
        ];
        const headerRow2 = [];

        examHeadersData.forEach((exam) => {
          headerRow1.push({
            content: exam.examType,
            colSpan: exam.subCategories.length,
            styles: { halign: "center" },
          });
          exam.subCategories.forEach((sub) => {
            headerRow2.push({
              content: `${sub.name}\n(Max:${sub.maxMarks})`,
              styles: { halign: "center" },
            });
          });
        });

        headerRow1.push(
          { content: "Total\nObt", rowSpan: 2, styles: { halign: "center" } },
          { content: "Total\nMax", rowSpan: 2, styles: { halign: "center" } },
          { content: "Grade", rowSpan: 2, styles: { halign: "center" } }
        );

        const tableHeaders = [headerRow1, headerRow2];

        // Build data rows using API-provided totals for each subject
        const tableData = subjectNames.map((subject, idx) => {
          const row = [idx + 1, subject];

          examHeadersData.forEach((exam) => {
            exam.subCategories.forEach((sub) => {
              const mark = marksData[subject]?.[exam.examType]?.[sub.name];
              row.push(formatMarks(mark));
            });
          });

          const totals = subjectTotalsMap[subject];
          row.push(formatMarks(totals?.totalObtainedMarks));
          row.push(formatMarks(totals?.totalMaxMarks));
          row.push(totals?.totalGrade ?? "--");

          return row;
        });

        // Table sizing
        const totalColumns =
          2 +
          examHeadersData.reduce((sum, e) => sum + e.subCategories.length, 0) +
          3; // S.No + Subject + Total Obt + Total Max + Grade
        const gradeColIndex = totalColumns - 1;

        const snoColW = 12;
        const subjectColW = 34;
        const fixedColW = 18; // Total Obt, Total Max, Grade
        const available = pageWidth - 2 * minMargin - snoColW - subjectColW - 3 * fixedColW;
        const subExamColW = Math.min(22, available / (totalColumns - 5));
        const tableWidth = snoColW + subjectColW + (totalColumns - 5) * subExamColW + 3 * fixedColW;
        const marginLeft = (pageWidth - tableWidth) / 2;

        const columnStyles = {
          0: { cellWidth: snoColW },
          1: { cellWidth: subjectColW },
        };
        let colIdx = 2;
        examHeadersData.forEach((exam) => {
          exam.subCategories.forEach(() => {
            columnStyles[colIdx++] = { cellWidth: subExamColW };
          });
        });
        columnStyles[colIdx] = { cellWidth: fixedColW };
        columnStyles[colIdx + 1] = { cellWidth: fixedColW };
        columnStyles[colIdx + 2] = { cellWidth: fixedColW };

        doc.autoTable({
          startY: lastY,
          head: tableHeaders,
          body: tableData,
          styles: { fontSize: 8.5, halign: "center", font: "times" },
          headStyles: {
            fillColor: NAVY,
            textColor: [255, 255, 255],
            lineWidth: 0.5,
            font: "times",
            fontStyle: "bold",
          },
          alternateRowStyles: { fillColor: LIGHT_BG },
          columnStyles,
          theme: "grid",
          margin: { left: marginLeft, right: marginLeft },
          tableWidth,
          // Two-tone header (navy for exam-type row, teal for sub-exam row)
          // + color-coded grade column in the body
          didParseCell: (data) => {
            if (data.section === "head" && data.row.index === 1) {
              data.cell.styles.fillColor = TEAL;
            }
            if (data.section === "body" && data.column.index === gradeColIndex) {
              data.cell.styles.textColor = gradeColor(data.cell.raw);
              data.cell.styles.fontStyle = "bold";
            }
          },
        });

        lastY = doc.lastAutoTable.finalY + 8;
      }

      // ── Remarks & Overall Grade ───────────────────────────────────────────
      doc.setFontSize(10);
      doc.setFont("times", "bold");
      doc.text("Remarks:", 15, lastY);
      doc.setFont("times", "normal");
      doc.text(`${student.teacherRemarks ?? "N/A"}`, 38, lastY);

      const gradeText = `${student.grade ?? "N/A"} (${formatPercent(student.totalPercentage)})`;

      const rightMargin = 20;
      const labelX =
        pageWidth - rightMargin - doc.getTextWidth(gradeText) - doc.getTextWidth("Overall Grade: ") - 5;

      doc.setFont("times", "bold");
      doc.text("Overall Grade:", labelX, lastY);
      doc.setFont("times", "normal");
      doc.setTextColor(...gradeColor(student.grade));
      doc.text(gradeText, pageWidth - rightMargin, lastY, null, null, "right");
      doc.setTextColor(0, 0, 0);

      // ── Attendance Table ──────────────────────────────────────────────────
      const attendanceTableY = lastY + 8;
      const attendanceHeaders = [
        [
          { content: "Total Days", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Present", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Absent", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Holidays", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Recorded Days", styles: { halign: "center", font: "times", fontStyle: "bold" } },
          { content: "Attendance %", styles: { halign: "center", font: "times", fontStyle: "bold" } },
        ],
      ];

      // attendance comes from totalAttendancList[0] — attendancePercentage is a number here
      const attendanceData = [
        [
          attendance?.totalDays ?? "N/A",
          attendance?.presentCount ?? "N/A",
          attendance?.absentCount ?? "N/A",
          attendance?.holidayCount ?? "N/A",
          attendance?.recordedDays ?? "N/A",
          attendance?.attendancePercentage != null ? formatPercent(attendance.attendancePercentage) : "N/A",
        ],
      ];

      const attendanceTableWidth = 250;
      const attendanceMarginLeft = (pageWidth - attendanceTableWidth) / 2;
      const attendanceCellW = attendanceTableWidth / 6;

      doc.autoTable({
        startY: attendanceTableY,
        head: attendanceHeaders,
        body: attendanceData,
        styles: { fontSize: 9, halign: "center", font: "times" },
        headStyles: {
          fillColor: NAVY,
          textColor: [255, 255, 255],
          lineWidth: 0.5,
          font: "times",
          fontStyle: "bold",
        },
        columnStyles: Object.fromEntries(
          Array.from({ length: 6 }, (_, i) => [i, { cellWidth: attendanceCellW }])
        ),
        theme: "grid",
        margin: { left: attendanceMarginLeft, right: attendanceMarginLeft },
        tableWidth: attendanceTableWidth,
      });

      lastY = doc.lastAutoTable.finalY + 8;

      // ── Grading Scale Table ───────────────────────────────────────────────
      const gradingHeaders = [
        [
          {
            content: "Marks Range %",
            styles: {
              halign: "center",
              fillColor: NAVY,
              textColor: [255, 255, 255],
              fontStyle: "bold",
              fontSize: 8,
              font: "times",
            },
          },
          "91-100", "81-90", "71-80", "61-70", "51-60", "41-50", "32-40", "Below 32",
        ],
      ];
      const gradingData = [
        [
          { content: "Grade", styles: { halign: "center", fillColor: [240, 240, 240], fontStyle: "bold", fontSize: 8, font: "times" } },
          "A+", "A", "B+", "B", "C+", "C", "D", "E",
        ],
      ];

      const gradingTableWidth = 257;
      const gradingMarginLeft = (pageWidth - gradingTableWidth) / 2;

      doc.autoTable({
        startY: lastY,
        head: gradingHeaders,
        body: gradingData,
        styles: { fontSize: 7.5, halign: "center", cellPadding: 3, font: "times" },
        theme: "grid",
        headStyles: { fillColor: TEAL, textColor: [255, 255, 255], font: "times" },
        tableWidth: gradingTableWidth,
        margin: { left: gradingMarginLeft },
      });

      // ── Signature Lines & Footer ─────────────────────────────────────────
      const signatureY = doc.lastAutoTable.finalY + 18;
      doc.setFont("times", "normal");
      doc.setFontSize(10);

      doc.setDrawColor(...TEAL);
      doc.setLineWidth(0.3);
      doc.line(15, signatureY, 80, signatureY);
      doc.text("Signature of Class Teacher", 20, signatureY + 6);

      doc.line(pageWidth - 80, signatureY, pageWidth - 15, signatureY);
      doc.text("Signature of Principal", pageWidth - 20, signatureY + 6, null, null, "right");

      // Generation timestamp footer, for authenticity/audit trail
      doc.setFontSize(7.5);
      doc.setTextColor(120, 120, 120);
      doc.text(
        `Generated on ${new Date().toLocaleString()}`,
        pageWidth / 2,
        pageHeight - borderMargin - 3,
        null,
        null,
        "center"
      );
      doc.setTextColor(0, 0, 0);

      setIsPdfLoading(false);
      const pdfUrl = doc.output("bloburl");
      window.open(pdfUrl, "_blank");
    } catch (error) {
      setIsPdfLoading(false);
      alert("Error generating PDF: " + error.message);
      console.error("Error generating PDF:", error);
    }
  };

  const handleSessionChange = (event) => setSession(event.target.value);
  const handleExamTypeChange = (event) => setExamType(event.target.value);
  const handleSectionChange = (event) => setSection(event.target.value);

  useEffect(() => { fetchFormId(); }, []);
  useEffect(() => { if (formId) fetchPermissions(); }, [formId]);
  useEffect(() => {
    if (permissions.isPermission && permissions.uRead) {
      fetchSessions();
      fetchExamTypes();
      fetchClasses();
    }
  }, [permissions]);

  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </div>
    );
  }

  if (error || !permissions.isPermission) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <Typography variant="h4" color="error">You are not authorised to view this page</Typography>
      </div>
    );
  }

  return (
    <>
      <Box sx={{ backgroundColor: "#0B1F40", padding: "12px 24px", boxShadow: 3, borderRadius: "4px 4px 0 0", margin: "2px auto", maxWidth: "900px" }}>
        <Typography variant="h4" sx={{ color: "#fff", textAlign: "center", fontWeight: "bold" }}>
          Generated Result{" "}
          <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6", cursor: "pointer" }} />
        </Typography>
      </Box>

      <Dialog open={showUserGuide} onClose={() => setShowUserGuide(false)} maxWidth="sm" fullWidth
        sx={{ "& .MuiDialog-paper": { borderRadius: "12px", boxShadow: "0 8px 24px rgba(0,0,0,0.2)", backgroundColor: "#f9fafb" } }}>
        <DialogTitle sx={{ background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)", color: "white", fontWeight: "bold", textAlign: "center", borderRadius: "12px 12px 0 0", py: 2 }}>
          Result User Guide
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography variant="h6" sx={{ color: "#1898c2", fontWeight: "600", mb: 2, mt: 1, textAlign: "center" }}>
            Quick Steps to Get and Print Result
          </Typography>
          <Box component="ul" sx={{ listStyleType: "none", padding: 0, "& li": { display: "flex", alignItems: "flex-start", mb: 1.5, fontSize: "0.9rem", color: "#333" }, "& li::before": { content: '"\\2022"', color: "#1898c2", fontWeight: "bold", display: "inline-block", width: "1.5em", mr: 1, fontSize: "1.2rem" }, "& strong": { margin: "0 0.2em" } }}>
            <li>Select a <strong>Session</strong> from the dropdown.</li>
            <li>Select a <strong>Class</strong> from the dropdown.</li>
            <li>Select a <strong>Section</strong> from the dropdown.</li>
            <li>Click <strong>Get Result</strong> to load the student list.</li>
            <li>Click <strong>Print</strong> next to a student to generate their PDF report.</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <StyledButton onClick={() => setShowUserGuide(false)} color="primary" variant="contained"
            sx={{ backgroundColor: "#1898c2", "&:hover": { backgroundColor: "#156f9a" }, borderRadius: "8px", px: 4 }}>
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} style={{ padding: "20px", margin: "10px auto", maxWidth: "900px" }}>
        <LoadingBar progress={loadingBarProgress} color="rgb(152, 106, 182)" height={5} />

        <Grid container spacing={2} justifyContent="center" sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Select value={session} onChange={handleSessionChange} displayEmpty sx={{ height: 45 }}>
                <MenuItem value="" disabled>Select Session</MenuItem>
                {sessions.map((item) => (
                  <MenuItem key={item.financialYearID} value={item.financialYearID}>{item.finanacialYear}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Select value={className} onChange={handleClassChange} displayEmpty sx={{ height: 45 }}>
                <MenuItem value="" disabled>Select Class</MenuItem>
                {classes.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <Select value={section} onChange={handleSectionChange} displayEmpty sx={{ height: 45 }} disabled={!className}>
                <MenuItem value="" disabled>Select Section</MenuItem>
                {sections.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <Button variant="contained" onClick={handleGetResult} sx={{ width: "200px", height: 45 }}>
            Get Result
          </Button>
        </Box>

        <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
          <Alert onClose={() => setOpenSnackbar(false)} severity="warning" sx={{ width: "100%" }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>

        <Box sx={{ mt: 3 }}>
          {permissions.uRead ? (
            isLoading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="150px">
                <CircularProgress color="primary" />
                <Typography variant="body1" sx={{ ml: 2 }}>Fetching result data...</Typography>
              </Box>
            ) : resultData.length > 0 ? (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, textAlign: "center" }}>Result Data</Typography>
                <Table sx={{ border: "1px solid #ccc" }}>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                      <TableCell align="center">Roll No</TableCell>
                      <TableCell align="center">Student Name</TableCell>
                      <TableCell align="center">Total Marks</TableCell>
                      <TableCell align="center">Obtained Marks</TableCell>
                      <TableCell align="center">Grade</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resultData.map((student, index) => (
                      <TableRow key={index} sx={{ "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" }, "&:nth-of-type(even)": { backgroundColor: "#ffffff" } }}>
                        <TableCell align="center">{student.rollNo}</TableCell>
                        <TableCell align="center">{student.studentName}</TableCell>
                        <TableCell align="center">{student.totalMaxMarks}</TableCell>
                        <TableCell align="center">{student.totalObtainedMarks}</TableCell>
                        <TableCell align="center">{student.grade}</TableCell>
                        <TableCell align="center">
                          <Tooltip title={permissions.uWrite ? "" : "You are not authorised to print results"}>
                            <span>
                              <Button
                                onClick={() => fetchStudentProfile(student.studentId)}
                                style={{ padding: "5px 10px", backgroundColor: permissions.uWrite ? "#007BFF" : "#ccc", color: "#fff", border: "none", borderRadius: "5px", cursor: permissions.uWrite ? "pointer" : "not-allowed" }}
                                disabled={!permissions.uWrite}
                              >
                                Print
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body1" sx={{ textAlign: "center", mt: 2 }}>No data to display.</Typography>
            )
          ) : (
            <Typography variant="h6" sx={{ textAlign: "center", mt: 2 }}>You are not authorised to view results</Typography>
          )}
        </Box>
      </Paper>

      <Dialog open={isPdfLoading}
        PaperProps={{ style: { background: "transparent", boxShadow: "none", overflow: "hidden" } }}
        sx={{ "& .MuiDialog-container": { alignItems: "center", justifyContent: "center" } }}>
        <DialogContent sx={{ background: "white", padding: "20px", borderRadius: "8px", textAlign: "center", minWidth: "200px" }}>
          <div style={{ width: "40px", height: "40px", border: "4px solid #f3f3f3", borderTop: "4px solid #3498db", borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 15px" }} />
          <Typography>Generating PDF, please wait...</Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GeneratedResult;